import { useEffect, useSyncExternalStore } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { importRunStore } from "@/lib/import/importRunStore";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Loader2, X } from "lucide-react";

/**
 * Floating progress dock for a bulk model import that is still running while
 * the user browses other pages. Hidden on the import page itself, which shows
 * its own inline progress.
 */
export const ImportProgressDock = () => {
  const run = useSyncExternalStore(importRunStore.subscribe, importRunStore.getSnapshot);
  const location = useLocation();
  const navigate = useNavigate();

  // App-wide unload guard: the upload lives in the tab, so closing or
  // refreshing it interrupts the run no matter which page is showing.
  useEffect(() => {
    if (!run.active) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [run.active]);

  if (!run.active) return null;

  if (location.pathname.startsWith("/agency/import") || location.pathname.startsWith("/agencies/import")) {
    return null;
  }

  const summary = run.summary;
  const counts =
    summary?.totalFiles && summary.totalFiles > 0
      ? `${summary.uploadedFiles ?? 0} of ${summary.totalFiles}`
      : null;

  return (
    <div className="fixed bottom-4 right-4 z-50 w-[19rem] rounded-xl border bg-card p-4 shadow-lg">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm font-medium">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            Importing models
          </p>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {summary?.currentFile || summary?.fileName || "Working..."}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          aria-label="Cancel import"
          onClick={() => importRunStore.requestCancel()}
          disabled={run.cancelRequested}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <Progress value={run.progress} className="mt-3 h-1.5" />

      <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
        <span>{run.cancelRequested ? "Finishing up..." : counts ?? `${run.progress}%`}</span>
        <button
          type="button"
          className="font-medium text-foreground underline-offset-2 hover:underline"
          onClick={() => navigate("/agency/import")}
        >
          View
        </button>
      </div>

      <p className="mt-2 text-xs leading-snug text-muted-foreground">
        Keep this tab open - closing it stops the upload.
      </p>
    </div>
  );
};
